/**
 * Horizontal mini-workflow bar for a dossier tab.
 * Click a step to cycle its status: pending → in_progress → done.
 * Reusable across all tabs (Vérif docs, Finance, Démarches, Installation, Compta, Documents).
 */
import React from "react";
import { Check, Circle, PlayCircle, Lock } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const STATUS_CYCLE = ["pending", "in_progress", "done"];
const STATUS_STYLES = {
  pending: { bg: "bg-norea-surfaceHover", border: "border-norea-border", text: "text-norea-textMuted", dot: "bg-norea-border", Icon: Circle },
  in_progress: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", dot: "bg-amber-500", Icon: PlayCircle },
  done: { bg: "bg-emerald-50", border: "border-emerald-400", text: "text-emerald-800", dot: "bg-emerald-500", Icon: Check },
  blocked: { bg: "bg-red-50", border: "border-red-300", text: "text-red-800", dot: "bg-red-500", Icon: Lock },
};

export default function TabWorkflowBar({ steps, statuses, dossierId, tab, onChange }) {
  if (!steps || steps.length === 0) return null;

  const cycle = async (stepId) => {
    const current = statuses?.[stepId] || "pending";
    const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
    try {
      await api.post(`/dossiers/${dossierId}/tab-data/${tab}/workflow`, { step_id: stepId, status: next });
      onChange?.();
    } catch {
      toast.error("Impossible de mettre à jour l'étape");
    }
  };

  return (
    <div
      className="flex items-center gap-2 overflow-x-auto pb-2 mb-5"
      data-testid={`tab-workflow-${tab}`}
    >
      {steps.map((step, i) => {
        const status = statuses?.[step.id] || "pending";
        const style = STATUS_STYLES[status] || STATUS_STYLES.pending;
        const Icon = style.Icon;
        return (
          <React.Fragment key={step.id}>
            <button
              type="button"
              onClick={() => cycle(step.id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md border transition hover:ring-2 hover:ring-norea-primary/30 ${style.bg} ${style.border}`}
              data-testid={`workflow-step-${tab}-${step.id}`}
              title="Cliquer pour avancer : à faire → en cours → terminé"
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${style.dot} text-white`}>
                <Icon className="w-3 h-3" />
              </span>
              <span className={`text-xs font-medium ${style.text} whitespace-nowrap`}>{step.label}</span>
            </button>
            {i < steps.length - 1 && (
              <div className="w-4 h-0.5 bg-norea-border flex-shrink-0" aria-hidden="true" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
